import { useMemo } from 'react';

import { SortingState } from '@cast/design-system';
import { WorkloadOptimizationState, WorkloadRuntime } from '@cast/types';

import { PreparedWorkload } from '../types';
import { prepareWorkload } from '../utils';
import { usePaginatedWoopWorkloads } from './usePaginatedWoopWorkloads';
import { useWorkloadScalingPolicies } from './useWorkloadScalingPolicies';

type Args = {
  enabled?: boolean;
  pageLimit?: number;
  managementOptions?: WorkloadOptimizationState[];
  workloadIds?: string[];
  workloadNames?: string[];
  namespaces?: string[];
  scalingPolicyNames?: string[];
  kinds?: string[];
  sortingState?: SortingState;
  anyContainerWithRuntime?: WorkloadRuntime;
};

export const usePaginatedOptimizationWorkloads = ({
  enabled = true,
  pageLimit,
  managementOptions,
  workloadIds,
  workloadNames,
  namespaces,
  scalingPolicyNames,
  kinds,
  sortingState,
  anyContainerWithRuntime,
}: Args = {}) => {
  const {
    policies,
    isLoading: policiesLoading,
    refetch: refetchPolicies,
  } = useWorkloadScalingPolicies();

  const {
    workloads,
    isLoading: workloadsLoading,
    error,
    refetch: refetchWorkloads,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = usePaginatedWoopWorkloads({
    enabled,
    pageLimit,
    managementOptions,
    workloadIds,
    workloadNames,
    namespaces,
    scalingPolicyNames,
    kinds,
    sortField: sortingState?.columnId ?? 'tableView',
    sortOrder: sortingState?.sortBy?.direction ?? 'asc',
    anyContainerWithRuntime,
  });

  const preparedWorkloads = useMemo<PreparedWorkload[]>(() => {
    if (policiesLoading) {
      return [];
    }
    return workloads?.map((w) => prepareWorkload(w, policies || [])) || [];
  }, [workloads, policies, policiesLoading]);

  return {
    workloads: preparedWorkloads,
    isLoading: policiesLoading || workloadsLoading,
    error,
    refetch: () => {
      refetchPolicies();
      refetchWorkloads();
    },
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
